// raceGeometry.js — the race chart's maths, shared by the on-screen SVG
// chart (RaceChart.js) and the downloadable video (raceVideo.js) so both
// draw the same frame for the same playhead: timing, the Follow window,
// the value axis, ticks, the monotone line paths, line heads and the
// leaderboard order. Nothing here touches React or the DOM.

export const SECONDS_PER_SEASON = 25; // at 1x
export const MAX_SECONDS = 120; // at 1x, however long the range
export const CURVE_MAX_STEPS = 400; // wider views use straight segments, thinned to one point per pixel
export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const has = (v) => v !== null && v !== undefined;

export const fmtDay = (date) => {
  const d = new Date(`${date}T00:00:00Z`);
  return `${DAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

export const seasonsIn = (race) => new Set(race.days.map((d) => d.season)).size || 1;

// How long playing the whole range takes at 1x, in seconds.
export const playSeconds = (race) => Math.min(MAX_SECONDS, SECONDS_PER_SEASON * seasonsIn(race));

export function niceTicks(min, max, count = 5) {
  if (max === min) return [min];
  const raw = (max - min) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => raw <= s) || 10 * mag;
  const out = [];
  for (let v = Math.ceil(min / step) * step; v <= max + 1e-9; v += step) out.push(Math.round(v * 1000) / 1000 || 0); // no "-0"
  return out;
}

// Fritsch–Carlson monotone tangents for one series (per contiguous run of
// defined values), so curves never overshoot the match-day values.
export function tangents(vals) {
  const n = vals.length;
  const m = new Array(n).fill(0);
  let k = 0;
  while (k < n) {
    if (!has(vals[k])) {
      k++;
      continue;
    }
    let end = k;
    while (end + 1 < n && has(vals[end + 1])) end++;
    if (end > k) {
      const d = [];
      for (let i = k; i < end; i++) d.push(vals[i + 1] - vals[i]);
      m[k] = d[0];
      m[end] = d[d.length - 1];
      for (let i = k + 1; i < end; i++) {
        const a = d[i - k - 1];
        const b = d[i - k];
        m[i] = a * b <= 0 ? 0 : (a + b) / 2;
      }
      for (let i = k; i < end; i++) {
        const di = d[i - k];
        if (di === 0) {
          m[i] = 0;
          m[i + 1] = 0;
          continue;
        }
        const a = m[i] / di;
        const b = m[i + 1] / di;
        const s = a * a + b * b;
        if (s > 9) {
          const t = 3 / Math.sqrt(s);
          m[i] = t * a * di;
          m[i + 1] = t * b * di;
        }
      }
    }
    k = end + 1;
  }
  return m;
}

// Value between days w and w+1 at fraction t (cubic Hermite).
const hermite = (v0, v1, m0, m1, t) => {
  const t2 = t * t;
  const t3 = t2 * t;
  return (2 * t3 - 3 * t2 + 1) * v0 + (t3 - 2 * t2 + t) * m0 + (-2 * t3 + 3 * t2) * v1 + (t3 - t2) * m1;
};

// Every selected club's full series (null where undefined) and its curve tangents.
export function buildSeries(race, selected, metric) {
  const n = race.days.length;
  return selected.map((i) => {
    const s = race.stats[i];
    const vals = new Array(n);
    for (let w = 0; w < n; w++) vals[w] = metric.value(s, w, race, i);
    return { i, vals, m: tangents(vals) };
  });
}

// A series' value at a fractional match day (on its curve), or null.
export function valueAt(s, w, t) {
  const v0 = s.vals[w];
  if (!has(v0)) return null;
  if (t <= 0 || w + 1 >= s.vals.length || !has(s.vals[w + 1])) return v0;
  return hermite(v0, s.vals[w + 1], s.m[w], s.m[w + 1], t);
}

// What's visible at playhead pos: in Follow, a trailing window of ~1.5
// seasons of match days (panning with the playhead), else all of them.
export function raceWindow(n, seasons, pos, mode) {
  const p = Math.max(0, Math.min(n - 1, pos));
  const cur = Math.floor(p);
  const frac = cur >= n - 1 ? 0 : p - cur;
  const windowW = Math.min(n, Math.max(60, Math.round((n / seasons) * 1.5)));
  const follow = mode === "follow" && windowW < n;
  const xStart = follow ? Math.max(0, Math.min(p - (windowW - 1), n - windowW)) : 0;
  const xEnd = follow ? xStart + windowW - 1 : n - 1;
  return { n, pos: p, cur, frac, windowW, follow, xStart, xEnd, firstW: Math.floor(xStart), lastW: Math.min(n - 1, Math.ceil(xEnd)) };
}

// The value axis the view wants: fitted to what's drawn in Follow, from
// zero (or 1st) in Whole period. Callers ease toward it while playing.
export function targetDomain(series, metric, mode, view) {
  const { firstW, cur, frac } = view;
  let tMin = Infinity;
  let tMax = -Infinity;
  const see = (v) => {
    if (!has(v)) return;
    if (v < tMin) tMin = v;
    if (v > tMax) tMax = v;
  };
  for (const s of series) {
    for (let w = firstW; w <= cur; w++) see(s.vals[w]);
    see(valueAt(s, cur, frac));
  }
  if (tMin === Infinity) {
    tMin = 0;
    tMax = 1;
  }
  if (metric.key === "pos") {
    if (mode === "follow") {
      tMin = Math.max(0.5, tMin - 0.5);
      tMax += 0.5;
    } else {
      tMin = 1;
      tMax = Math.max(tMax, 2);
    }
  } else if (mode === "follow") {
    const pad = Math.max((tMax - tMin) * 0.08, metric.key === "ppg" ? 0.05 : 1);
    tMin -= pad;
    tMax += pad;
    if (metric.key !== "gd") tMin = Math.max(0, tMin); // only goal difference goes negative
  } else {
    tMin = Math.min(0, tMin);
    if (tMax === tMin) tMax = tMin + 1;
  }
  return { min: tMin, max: tMax };
}

// Screen mappings for a plot box { left, top, width, height }.
export function scales(view, box, domain, invert) {
  const { xStart, xEnd } = view;
  const x = (w) => box.left + (xEnd === xStart ? box.width / 2 : ((w - xStart) / (xEnd - xStart)) * box.width);
  const span = domain.max - domain.min;
  const y = (v) => (invert ? box.top + ((v - domain.min) / span) * box.height : box.top + box.height - ((v - domain.min) / span) * box.height);
  return { x, y };
}

export const yTicksFor = (metric, domain) =>
  metric.key === "pos"
    ? niceTicks(Math.ceil(domain.min), Math.floor(domain.max), 5).filter((t) => Number.isInteger(t) && t >= 1)
    : niceTicks(domain.min, domain.max, 5);

// Time axis: seasons when many are visible, months otherwise. Which
// seasons/months get a label depends on the season or month itself, so
// labels slide with the window instead of flickering. `scale` widens the
// label spacing for bigger text.
export function xTicksFor(race, view, plotW, scale = 1) {
  const { firstW, lastW, xStart, xEnd } = view;
  const vis = race.days.slice(firstW, lastW + 1);
  const seasons = [...new Set(vis.map((d) => d.season))];
  if (seasons.length > 3) {
    const every = Math.max(1, Math.ceil(seasons.length / Math.max(2, Math.floor(plotW / (56 * scale)))));
    return seasons
      .filter((s) => s % every === 0)
      .map((s) => ({ w: race.days.findIndex((d) => d.season === s), label: `${String(s % 100).padStart(2, "0")}/${String((s + 1) % 100).padStart(2, "0")}` }))
      .filter((t) => t.w >= xStart && t.w <= xEnd);
  }
  const months = [];
  let lastKey = "";
  for (let w = firstW; w <= lastW; w++) {
    const d = new Date(`${race.days[w].date}T00:00:00Z`);
    const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
    if (key !== lastKey) months.push({ w, month: d.getUTCMonth(), year: d.getUTCFullYear() });
    lastKey = key;
  }
  // Every 1, 2, 3, 4, 6 or 12 months counted from August, so labels land on
  // months with matches (Aug and Feb, not Jan and Jul). Summers have no
  // match days, so a label late in a season (Apr) would sit next to the
  // following Aug: it's left out when it's under two thirds of a step away.
  const need = months.length / Math.max(2, Math.floor(plotW / ((seasons.length > 1 ? 62 : 44) * scale)));
  const every = [1, 2, 3, 4, 6, 12].find((k) => k >= need) || 12;
  const fromAug = (month) => (month + 5) % 12;
  return months
    .filter((mo) => fromAug(mo.month) % every === 0 && (every === 1 || 10 - fromAug(mo.month) >= every * 0.66))
    .filter((mo) => mo.w >= xStart && mo.w <= xEnd)
    .map((mo) => ({ w: mo.w, label: seasons.length > 1 ? `${MONTHS[mo.month]} ’${String(mo.year % 100).padStart(2, "0")}` : MONTHS[mo.month] }));
}

// SVG path data (also fine for Path2D) from the window's first match day
// to the playhead: monotone cubic segments (straight ones in very wide
// views, at most one point per pixel), ending part-way along the current
// segment.
export function linePath(s, view, x, y) {
  const { n, firstW, cur, frac, xStart, xEnd } = view;
  const curves = xEnd - xStart <= CURVE_MAX_STEPS;
  const X = (w) => x(w).toFixed(1);
  const Y = (v) => y(v).toFixed(1);
  let d = "";
  let pen = false;
  let lastPx = -Infinity;
  for (let w = firstW; w <= cur; w++) {
    const v = s.vals[w];
    if (!has(v)) {
      pen = false;
      continue;
    }
    if (!pen) {
      d += `M${X(w)},${Y(v)}`;
      pen = true;
      lastPx = x(w);
    }
    const last = w === cur;
    const t = last ? frac : 1;
    if (t <= 0 || w + 1 >= n || !has(s.vals[w + 1])) continue;
    const v1 = s.vals[w + 1];
    if (!curves) {
      const px = x(w + t);
      if (px - lastPx < 1 && !last && w + 1 < cur) continue; // thin to one point per pixel
      d += `L${px.toFixed(1)},${Y(last ? v + (v1 - v) * t : v1)}`;
      lastPx = px;
      continue;
    }
    // Bezier control points (value space), split at t for the last segment.
    const p0 = [w, v];
    const p1 = [w + 1 / 3, v + s.m[w] / 3];
    const p2 = [w + 2 / 3, v1 - s.m[w + 1] / 3];
    const p3 = [w + 1, v1];
    if (t >= 1) {
      d += `C${X(p1[0])},${Y(p1[1])} ${X(p2[0])},${Y(p2[1])} ${X(p3[0])},${Y(p3[1])}`;
    } else {
      const lerp = (a, b) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
      const q1 = lerp(p0, p1);
      const r = lerp(p1, p2);
      const q2 = lerp(q1, r);
      const q3 = lerp(q2, lerp(r, lerp(p2, p3)));
      d += `C${X(q1[0])},${Y(q1[1])} ${X(q2[0])},${Y(q2[1])} ${X(q3[0])},${Y(q3[1])}`;
    }
  }
  return d;
}

// Line heads at the playhead (on the curve), with label rows nudged at
// least `gap` apart and kept between minY and maxY. Labels show the
// playhead day's value (v); positions use the curve (hv).
export function placeHeads(series, view, x, y, gap, minY, maxY) {
  const { cur, frac } = view;
  const heads = series
    .map((s, k) => ({ i: s.i, k, v: s.vals[cur], hv: valueAt(s, cur, frac) }))
    .filter((h) => has(h.v) && has(h.hv))
    .map((h) => ({ ...h, hx: x(cur + frac), hy: y(h.hv) }))
    .sort((a, b) => a.hy - b.hy);
  heads.forEach((h, j) => {
    h.ly = j === 0 ? Math.max(minY, h.hy) : Math.max(h.hy, heads[j - 1].ly + gap);
  });
  const overflow = heads.length ? heads[heads.length - 1].ly - maxY : 0;
  if (overflow > 0) heads.forEach((h) => (h.ly -= overflow));
  return heads;
}

// The leaderboard for match day `day`, best first. While playing it's
// ordered along the curve (at the playhead's fraction) so it re-sorts smoothly.
export function boardAt(series, metric, day, frac) {
  return series
    .map((s, k) => ({ i: s.i, k, v: s.vals[day], order: frac > 0 ? valueAt(s, day, frac) : s.vals[day] }))
    .filter((r) => has(r.v))
    .sort((a, b) => (metric.invert ? a.order - b.order : b.order - a.order));
}
